package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initMongknPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("mongkn.md", FrontMatterElement.Builder { addScalar("title", "mongkn"); addScalar("searchTitle", "MongoDB driver for Kotlin/Native — cinterop over libmongoc"); addScalar("description", "MongoDB where no official driver exists. A binding over libmongoc behind the shape of the coroutine driver's API, with read overhead indistinguishable from bare C."); addScalar("library", "mongkn"); addScalar("post", "/blog/bugs-that-dont-fail"); }))
}

@Page("/mongkn")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun MongknPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There is no official MongoDB driver for Kotlin/Native — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("mongodb-driver-kotlin-coroutine")
        }
        org.jetbrains.compose.web.dom.Text(" is JVM-only. mongkn writes the cinterop once and hides it behind ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("suspend fun insertOne(…)")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fun find(…): Flow<Document>")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("24 of 30 collection operations")
            }
            org.jetbrains.compose.web.dom.Text(": insert, update, delete, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("find")
            }
            org.jetbrains.compose.web.dom.Text(", aggregation, indexes, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("bulkWrite")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("findOneAnd*")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("distinct")
            }
            org.jetbrains.compose.web.dom.Text(". The six missing are missing on purpose — five are Atlas Search index operations, which do not exist on a self-hosted server, and the sixth is ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("mapReduce")
            }
            org.jetbrains.compose.web.dom.Text(", deprecated by MongoDB itself.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Sessions and transactions")
            }
            org.jetbrains.compose.web.dom.Text(", with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("withTransaction")
            }
            org.jetbrains.compose.web.dom.Text(" retrying on server error labels.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Change streams")
            }
            org.jetbrains.compose.web.dom.Text(" on collection, database and client, with automatic resumption.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("All topologies: standalone, replica set, sharded through ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("mongos")
            }
            org.jetbrains.compose.web.dom.Text(". SCRAM, TLS and x509, verified against servers started with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--auth")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--tlsMode requireTLS")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("18 of 20 BSON types")
            }
            org.jetbrains.compose.web.dom.Text(", its own tree model, and a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("kotlinx.serialization")
            }
            org.jetbrains.compose.web.dom.Text(" format on top.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("performance") }) {
        org.jetbrains.compose.web.dom.Text("Performance")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Overhead over the C driver it wraps, from a release binary against a local ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("mongo:8")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th()
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Write, per operation")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1470 µs against 1492 µs for bare C — below the measurement threshold")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Read into a class, per document")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.26 µs on Linux/x86_64, 1.66 µs on macOS/arm64")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Read overhead over a bare C cursor loop")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("−0.35 %, indistinguishable")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("kotlinx.serialization")
                    }
                    org.jetbrains.compose.web.dom.Text(" codec")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.27 µs, about 5 % of the mandatory conversion")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }
dependencies { implementation("io.github.youndie.mongkn:mongkn-core:<VERSION>") }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The write-up above is the one worth reading before you depend on this: four bugs that a full test suite and 71 specification scenarios did not catch, because none of them threw.")
    }
}
